// SPDX-FileCopyrightText: Copyright 2023 Dash0 Inc.

"use client";

import { LucideProps, SearchIcon } from "lucide-react";
import {
	ComponentProps,
	createContext,
	ForwardRefExoticComponent,
	KeyboardEvent,
	ReactNode,
	RefAttributes,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	useTransition,
} from "react";
import { useDebounceValue } from "usehooks-ts";

import { SearchWithoutBehavior } from "@/components/shared/search/search-without-behaviour";
import { mergeRefs } from "@/lib/utils";
import { UseSelector, useSelector } from "@/lib/utils/hooks/use-selector";

type SearchKeyboardNavigationState = {
	currentIndex: number;
};

const SEARCH_OFFSET_LEFT = 25;

export type SearchProps = Pick<ComponentProps<"div">, "className" | "onKeyDown"> & {
	/**
	 * When provided, the keys that scroll to prev/next match will attempt to scroll the passed in container.
	 */
	scrollContainerRef?: React.RefObject<HTMLDivElement>;
	inputRef?: React.RefObject<HTMLInputElement>;
	placeholder?: string;
	goToMatchOnEnter?: boolean;
	InputIcon?: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;
	inputClassName?: string;
	scrollCenter?: boolean;
	/**
	 * Shows a loading spinner in the search input.
	 */
	isLoading?: boolean;

	autoFocus?: boolean;

	additionalContent?: ReactNode;
};

export function Search({
	className,
	scrollContainerRef,
	onKeyDown: _onKeyDown,
	goToMatchOnEnter = true,
	placeholder,
	inputRef,
	InputIcon = SearchIcon,
	inputClassName,
	scrollCenter = false,
	isLoading,
	autoFocus,
	additionalContent,
}: SearchProps) {
	const searchCtx = useSearch();
	const localInputRef = useRef<HTMLInputElement>(null);
	const mergedRefs = mergeRefs(localInputRef, inputRef ?? null);

	/**
	 * Used to keep track of the current search match index for keyboard navigation.
	 */
	const searchKeyboardNavigationState = useRef<SearchKeyboardNavigationState>({
		currentIndex: -1,
	});
	const isEmpty = searchCtx?.search == null || searchCtx.search === "";

	/**
	 * Handles keyboard navigation for search matches. You can press up/down and
	 * shift-enter/enter to navigate to the prev/next match.
	 */
	const onKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			_onKeyDown?.(e);

			if (!goToMatchOnEnter) return;

			const currentIndex = searchKeyboardNavigationState.current.currentIndex;
			let nextIndex: number | null = null;

			if (e.key === "ArrowDown" || (e.key === "Enter" && !e.shiftKey)) {
				nextIndex = currentIndex + 1;
			} else if (e.key === "ArrowUp" || (e.key === "Enter" && e.shiftKey)) {
				nextIndex = currentIndex - 1;
			}

			if (nextIndex != null) {
				e.preventDefault();
				const matches = document.querySelectorAll(`.${searchMatchClassName}`);
				if (nextIndex < 0) {
					nextIndex = matches.length - 1;
				} else if (nextIndex >= matches.length) {
					nextIndex = 0;
				}

				for (const match of matches) {
					match.classList.remove("animate-highlight-search-term");
				}

				searchKeyboardNavigationState.current.currentIndex = nextIndex;
				const match = matches[nextIndex] as HTMLElement | undefined;
				if (match) {
					if (scrollContainerRef) {
						scrollContainerRef.current?.scrollTo({
							top: match.offsetTop,
							left: match.offsetLeft - SEARCH_OFFSET_LEFT,
							behavior: "smooth",
						});
						match.classList.add("animate-highlight-search-term");
					} else {
						match.scrollIntoView({ behavior: "smooth", block: scrollCenter ? "center" : "start" });
						match.classList.add("animate-highlight-search-term");
					}
				}
			}
		},
		[_onKeyDown, goToMatchOnEnter, scrollCenter, scrollContainerRef]
	);

	return (
		<SearchWithoutBehavior
			defaultValue={searchCtx?.search ?? ""}
			placeholder={placeholder}
			onChange={(value) => {
				searchCtx?.updateSearch(value);
				searchKeyboardNavigationState.current.currentIndex = -1;
			}}
			onKeyDown={onKeyDown}
			onClearClick={() => {
				// Clear the input field
				if (!localInputRef.current?.value) return;
				// Clear the input field
				localInputRef.current.value = "";
				// Restore the focus
				localInputRef.current.focus();
				// Update the search context
				searchCtx?.updateSearch("");
			}}
			className={className}
			InputIcon={InputIcon}
			ref={mergedRefs}
			inputClassName={inputClassName}
			isLoading={isLoading}
			autoFocus={autoFocus}
			showClear={!isEmpty}
			additionalContent={additionalContent}
		/>
	);
}

type SearchContext = {
	search: string | null;
	updateSearch: (value: string) => void;
};

const searchContext = createContext<SearchContext | null>(null);

export function SearchProvider({ children, defaultValue }: Readonly<{ children: ReactNode; defaultValue?: string }>) {
	const [search, setSearch] = useState<string | null>(defaultValue ?? null);
	const [_, startTransition] = useTransition();

	const updateSearch = useCallback((value: string) => {
		startTransition(() => {
			setSearch(value);
		});
	}, []);

	const value = useMemo(
		() => ({
			search,
			updateSearch,
		}),
		[search, updateSearch]
	);

	return <searchContext.Provider value={value}>{children}</searchContext.Provider>;
}

export function FixedSearchTermProvider({ children, searchTerm }: { children: ReactNode; searchTerm: string | null }) {
	const value = useMemo(
		() => ({
			search: searchTerm,
			updateSearch: () => {
				throw new Error("Cannot update search term in FixedSearchTermProvider");
			},
		}),
		[searchTerm]
	);

	return <searchContext.Provider value={value}>{children}</searchContext.Provider>;
}

export const useSearch: UseSelector<SearchContext | null> = (selector) => {
	return useSelector(useContext(searchContext), selector);
};

export function useSearchTerm(): string | null {
	const search = useSearch((s) => s?.search);
	return search?.toLowerCase() ?? null;
}

export function useDebouncedSearchTerm(): string {
	const searchTerm = useSearchTerm();
	const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
	return debouncedSearchTerm ?? "";
}

/**
 * This class name will be applied to all search matches. We can use it to jump to the prev/next match
 * using keyboard shortcuts.
 */
export const searchMatchClassName = "js-search-match";
